import { i18nDynamic } from 'shared/i18n';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef } from 'react';
import { Typography } from 'app/components/ui';
import { TagWithMetadata } from 'entities/tag';
import { TagItemShortcuts } from 'features/tagging/TagItemShortcuts/TagItemShortcuts';
import { resolveConceptLabelOrName } from 'app/utils/tag-utils';
import { useTagDisplay } from 'app/state/contexts/TagContext';
import { useGoToTag } from 'app/utils/hooks/useGoToTag';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';

interface TagListItemProps {
  tag: TagWithMetadata;
  selectedTag: TagWithMetadata | null;
  onSelectTag: (tag: TagWithMetadata | null) => void;
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
  onExtensionClick?: (tagId: string) => void;
  onDeleteClick?: (tag: TagWithMetadata) => void;
}

// Map API status values to CSS classes
const originColorClass: Record<string, string> = {
  human: 'all-tags-origin-human',
  ai: 'all-tags-origin-ai',
};

const statusColorClass: Record<string, string> = {
  approved: 'all-tags-status-approved',
  draft: 'all-tags-status-draft',
  verified: 'all-tags-status-verified',
  need_attention: 'all-tags-status-needs-attention',
};

// Map API validationStatus to border classes
const validationBorderClass: Record<string, string> = {
  valid: 'all-tags-item--border-ok',
  unvalidated: 'all-tags-item--border-warning',
  invalid: 'all-tags-item--border-error',
};

export const TagListItem: React.FC<TagListItemProps> = ({ tag, selectedTag, onSelectTag, onTagDetailsClick, onExtensionClick, onDeleteClick }) => {
  const { t } = useTranslation('tag');

  const originClass = tag.origin ? originColorClass[tag.origin] : '';
  const statusClass = tag.status ? statusColorClass[tag.status] : '';
  const borderClass = tag.validationStatus ? validationBorderClass[tag.validationStatus] || '' : '';
  const { tagLanguage } = useTagDisplay();
  const { goToTag, getGoToTagState } = useGoToTag();
  const goToState = getGoToTagState(tag);
  const isSelected = selectedTag?.id === tag.id;
  const label = resolveConceptLabelOrName({ concept: tag.concept, currentLang: tagLanguage });
  const itemRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isSelected]);

  return (
    <li
      ref={itemRef}
      className={`all-tags-item ${borderClass} ${isSelected ? 'all-tags-item--selected' : ''}`}
      tabIndex={0}
      onClick={() => {
        onSelectTag(tag);
        if (goToState.kind === 'scroll') {
          goToTag(tag);
        } else if (goToState.kind === 'navigate') {
          dispatchMessage(EventType.ADD_MESSAGE, {
            type: 'info',
            text: t('go.to.tag.other.section'),
            removeTime: 5000,
          });
        }
      }}
      onDoubleClick={() => console.log(`Tag ${tag.id} double clicked`)}
    >
      <div className="all-tags-item__header">
        <div className="all-tags-item__label">
          <Typography variant="body-7">{label || ''}</Typography>
        </div>

        <TagItemShortcuts
          actions={[
            { type: 'details', onClick: () => onTagDetailsClick?.(tag) },
            { type: 'navigate', onClick: () => goToTag(tag), disabled: goToState.disabled, tooltip: goToState.tooltip },
            ...(onExtensionClick ? [{ type: 'create' as const, onClick: () => onExtensionClick(tag.id) }] : []),
            ...(onDeleteClick ? [{ type: 'delete' as const, onClick: () => onDeleteClick(tag) }] : []),
          ]}
        />
      </div>

      <div className="all-tags-item__footer">
        <Typography variant="body-3" fontWeight="light" color="#979797">
          {tag.hidden
            ? t('hidden.title')
            : tag.metadata?.unplaced
            ? tag.metadata.tableName
              ? t('unplaced.table', { table: tag.metadata.tableName })
              : t('unplaced')
            : [
                t('location.section', { section: tag.metadata?.sectionName }),
                tag.metadata?.pageNumber ? t('location.page', { page: tag.metadata.pageNumber }) : null,
                tag.metadata?.tableName ? t('location.table', { table: tag.metadata.tableName }) : null,
              ]
                .filter(Boolean)
                .join(' | ')}
        </Typography>

        <div className="all-tags-item__tag-wrapper">
          <div aria-label="status-tag" className={`all-tags-item__status-tag ${statusClass}`}>
            <Typography variant="body-3" color="inherit">
              {t(`status.${tag.status}`)}
            </Typography>
          </div>

          <div aria-label="origin-tag" className={`all-tags-item__origin-tag ${originClass}`}>
            <Typography variant="body-3" color="inherit">
              {i18nDynamic(`tag:origin.${tag.origin}`)}
            </Typography>
          </div>
        </div>
      </div>
    </li>
  );
};
